import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { ExtendedMessageData } from 'common/store/genesys-cloud/conversations/messages/models/extended-message-data.model';
import * as moment from 'moment';
import {
  ConversationContentAttachment,
  ConversationContentAttachmentMediaType,
  ConversationMessageContentType,
  ConversationMessageEventPresenceType,
  ConversationMessageEventType,
  ConversationNormalizedMessageStatus
} from 'common/store/genesys-cloud/conversations/messages/models/message-data.model';
import { MessageDataSenderType } from 'common/store/genesys-cloud/conversations/common/models/message-data-sender-type.enum';
import { selectUser, selectUserNameById } from 'common/store/genesys-cloud/users/selectors/user.selectors';
import { Contact } from 'common/store/genesys-cloud/external-contacts/models/contact.model';
import { selectLastMessageStatusById } from 'common/store/genesys-cloud/conversations/messages/selectors/message.selectors';
import { map, Observable, of } from 'rxjs';
import { BcConfigService } from 'common/config/provider';
import { selectIsRichTextEnabled } from 'common/store/genesys-cloud/feature-toggles/selectors/footer-settings-selector';
import { selectFeatureToggle } from 'common/store/genesys-cloud/feature-toggles/selectors/feature-toggles.selectors';
import { FeatureToggles } from 'common/store/genesys-cloud/feature-toggles/model/feature-toggles.model';

@Component({
  selector: 'app-genesys-chat-message-list-item',
  templateUrl: './genesys-chat-message-list-item.component.html',
  styleUrls: ['./genesys-chat-message-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenesysChatMessageListItemComponent implements OnInit {
  readonly MessageDataSenderType = MessageDataSenderType;
  readonly ConversationMessageContentType = ConversationMessageContentType;
  readonly actualAgent$ = this.store.select(selectUser);
  private readonly _cardsAndCarouselsFT$ = this.store.select(selectFeatureToggle(FeatureToggles.CardsAndCarousels));

  shouldFormatMessage$: Observable<boolean>;
  isButtonResponse$: Observable<boolean>;
  isRichMediaType$: Observable<boolean>;

  @Input()
  message: ExtendedMessageData;
  @Input()
  contact: Contact;
  @Input()
  @HostBinding('class.first-segment-message')
  isFirstSegmentMessage: boolean;
  @Input()
  @HostBinding('class.last-segment-message')
  isLastSegmentMessage: boolean;
  @Input()
  attachments: ConversationContentAttachment[];
  @Output()
  imageLoad = new EventEmitter<void>();

  constructor(private readonly store: Store, private readonly config: BcConfigService) {}

  ngOnInit() {
    this.shouldFormatMessage$ = this.store.select(
      selectIsRichTextEnabled(this.message?.conversationId, this.config.current.genesysRichTextEnabledProviders)
    );

    this.setCardsAndCarouselsDisplay();
  }

  @HostBinding('class.participant-customer')
  get isCustomer(): boolean {
    return this.message?.senderType === MessageDataSenderType.Customer;
  }

  @HostBinding('class.participant-agent')
  get isAgent(): boolean {
    return this.message?.senderType === MessageDataSenderType.ActualAgent;
  }

  @HostBinding('class.participant-workflow')
  get isWorkflow(): boolean {
    return (
      this.message?.senderType === MessageDataSenderType.Bot ||
      this.message?.senderType === MessageDataSenderType.OtherAgent
    );
  }

  formatDate(dateString: string): string {
    return moment(dateString).format('LTS');
  }

  get isParticipantNameVisible(): boolean {
    return this.isWorkflow || this.isAgent;
  }

  get isCobrowse(): boolean {
    return (
      this.message?.normalizedMessage?.type === 'Event' &&
      this.message?.normalizedMessage?.events[0]?.eventType === 'CoBrowse'
    );
  }

  getUserName(userId: string) {
    return this.store.select(selectUserNameById(userId));
  }

  getLatestStatus$(): Observable<ConversationNormalizedMessageStatus> {
    return this.message.senderType === this.MessageDataSenderType.ActualAgent
      ? this.store.select(selectLastMessageStatusById(this.message.conversationId, this.message.id))
      : of(null);
  }

  shouldDisplaySignature(): boolean {
    return !this.isEventTypePresence && (this.isAgent || this.isLastSegmentMessage);
  }

  get imageAttachments() {
    return this.attachments?.filter((a) => a.mediaType === ConversationContentAttachmentMediaType.Image);
  }

  get urlBasedAttachments() {
    return this.attachments?.filter((a) => a.mediaType !== ConversationContentAttachmentMediaType.Image);
  }

  get isEventTypePresence() {
    return this.message.normalizedMessage?.events?.[0].eventType === ConversationMessageEventType.Presence;
  }

  get isConversationStartMessage() {
    return this.message.normalizedMessage?.events?.[0].presence?.type === ConversationMessageEventPresenceType.Join;
  }

  get isConversationEndMessage() {
    return (
      this.message.normalizedMessage?.events?.[0].presence?.type === ConversationMessageEventPresenceType.Disconnect
    );
  }

  get isConversationClearMessage() {
    return (
      this.message.normalizedMessage?.events?.[0].presence?.type === ConversationMessageEventPresenceType.Clear
    );
  }

  private setCardsAndCarouselsDisplay(): void {
    this.isButtonResponse$ = this._cardsAndCarouselsFT$.pipe(
      map((isAvailable) => {
        const content = this.message.normalizedMessage?.content;
        return isAvailable && content?.length ? content[0].buttonResponse?.type === 'Button' : false;
      })
    );

    this.isRichMediaType$ = this._cardsAndCarouselsFT$.pipe(
      map((isAvailable) => {
        const content = this.message.normalizedMessage?.content;
        return isAvailable && content
          ? content.some((c) =>
              [ConversationMessageContentType.Card, ConversationMessageContentType.Carousel].includes(c.contentType)
            )
          : false;
      })
    );
  }
}
